import { ComponentPropsWithoutRef, forwardRef } from "react";
import styles from "./PaginationItem.module.scss";
import cx from "classnames";
import { ButtonToggle } from "../Button";

/**
 * Props for the PaginationItem component
 * @extends ComponentPropsWithoutRef<"li">
 */
export type PaginationItemProps = ComponentPropsWithoutRef<"li"> & {
  /**
   * The page number to display.
   */
  page: number;
  /**
   * Whether this page item is currently active/selected.
   * @default false
   */
  isActive?: boolean;
  /**
   * Callback function called when the page item is clicked.
   */
  onClick?: () => void;
};

/**
 * PaginationItem component for displaying individual page numbers in pagination.
 *
 * Features:
 * - Displays page number as a toggle button
 * - Supports active state styling
 * - Fully accessible with proper ARIA attributes
 * - Integrates with pagination navigation system
 * - Consistent styling with design system
 *
 * @example
 * <PaginationItem
 *   page={3}
 *   isActive={true}
 *   onClick={() => console.log('Page 3 clicked')}
 * />
 */
export const PaginationItem = forwardRef<HTMLLIElement, PaginationItemProps>(
  (props, ref) => {
    const { page, isActive, onClick, className, ...rest } = props;

    const paginationClassName = cx(styles["pagination-item"], className);
    const paginationButtonClassName = cx(styles["pagination-item-button"], {
      [styles.active]: isActive,
    });
    return (
      <li ref={ref} {...rest} className={paginationClassName}>
        <ButtonToggle
          className={paginationButtonClassName}
          size="small"
          onClick={onClick}
          aria-current={isActive ? "page" : undefined}
          checked={isActive}
        >
          {page}
        </ButtonToggle>
      </li>
    );
  },
);

PaginationItem.displayName = "PaginationItem";
